import { ChevronRightIcon } from "lucide-react";
import { useState } from "react";

import { DetalleDeLlamada } from "@/components/laboratorio/DetalleDeLlamada";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Etiqueta } from "@/components/ui/etiqueta";
import { NativeSelect } from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useCorrida,
  useCorridas,
  useInformesDelLaboratorio,
  usePromptsDelLaboratorio,
  useRevisionesDelPrompt,
} from "@/hooks/useLaboratorio";
import { formatearCostoUsd } from "@/lib/formatoDeConsumo";
import {
  etiquetaDeRevision,
  fechaCortaDeLaCorrida,
  formatearDuracion,
  type CorridaDeLaLista,
  type EstadoDeLlamada,
  type EstadoFiltro,
  type FiltrosDeCorridas,
} from "@/lib/laboratorio";
import { cn } from "@/lib/utils";

const SIN_FILTROS: FiltrosDeCorridas = { informe: "", funcion: "", revision: "", estado: "cualquiera" };

const PUNTO: Record<EstadoDeLlamada, string> = { ok: "bg-primary", warn: "bg-[#e0a63a]", err: "bg-[#c23a32]" };

const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;

/**
 * Pestaña «Registro de corridas»: cada corrida de IA con sus pasos y llamadas,
 * y el detalle de la llamada elegida (entrada, salida y herramientas).
 */
export function RegistroDeCorridas({ onCasoCreado }: { onCasoCreado: () => void }) {
  const [filtros, setFiltros] = useState<FiltrosDeCorridas>(SIN_FILTROS);
  const [llamadaId, setLlamadaId] = useState<number | null>(null);
  const [corridaDeLaLlamada, setCorridaDeLaLlamada] = useState<number | null>(null);

  const corridas = useCorridas(filtros);
  const paginas = corridas.data?.pages ?? [];
  const filas = paginas.flatMap((p) => p.data);
  const total = paginas[0]?.meta.total ?? 0;
  const llamadasMostradas = paginas.reduce((n, p) => n + p.meta.llamadas_en_pagina, 0);

  return (
    <div className="flex flex-col gap-3.5">
      <Filtros filtros={filtros} onCambio={setFiltros} resumen={`${plural(total, "corrida", "corridas")} · ${plural(llamadasMostradas, "llamada", "llamadas")} mostradas`} />

      <div className="grid items-start gap-3.5 lg:grid-cols-[25rem_minmax(0,1fr)]">
        <Card variant="marcada" className="max-h-[40rem] gap-1.5 overflow-y-auto rounded-[1.25rem] p-2.5">
          {corridas.isPending ? (
            <Skeleton className="h-40" />
          ) : corridas.isError ? (
            <p className="text-meta text-danger-texto p-3">No pudimos cargar las corridas.</p>
          ) : filas.length === 0 ? (
            <p className="text-meta text-ink-medio p-6 text-center">Ninguna corrida coincide con estos filtros.</p>
          ) : (
            <ul aria-label="Corridas" className="flex flex-col gap-1.5">
              {filas.map((c) => (
                <li key={c.id}>
                  <TarjetaDeCorrida
                    corrida={c}
                    llamadaElegida={llamadaId}
                    onLlamada={(id) => {
                      setLlamadaId(id);
                      setCorridaDeLaLlamada(c.id);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
          {corridas.hasNextPage ? (
            <Button type="button" variant="ghost" size="sm" disabled={corridas.isFetchingNextPage} onClick={() => void corridas.fetchNextPage()}>
              Cargar más corridas
            </Button>
          ) : null}
        </Card>

        {llamadaId !== null ? (
          <DetalleDeLlamada key={llamadaId} id={llamadaId} corridaId={corridaDeLaLlamada} onCasoCreado={onCasoCreado} />
        ) : (
          <Card variant="marcada" className="text-meta text-ink-medio items-center rounded-[1.25rem] px-6 py-16 text-center">
            Elige una llamada para ver qué entró, qué salió y qué herramientas usó el agente.
          </Card>
        )}
      </div>
    </div>
  );
}

function Filtros({
  filtros,
  onCambio,
  resumen,
}: {
  filtros: FiltrosDeCorridas;
  onCambio: (f: FiltrosDeCorridas) => void;
  resumen: string;
}) {
  const informes = useInformesDelLaboratorio();
  const prompts = usePromptsDelLaboratorio();
  const revisiones = useRevisionesDelPrompt(filtros.funcion === "" ? null : Number(filtros.funcion));

  const campo = (_etiqueta: string, control: React.ReactNode) => <div className="w-44">{control}</div>;

  return (
    <div className="flex flex-wrap items-center gap-2.5">
      {campo(
        "Informe",
        <NativeSelect aria-label="Informe" size="sm" className="h-[2.375rem] rounded-md text-[0.78125rem]" value={filtros.informe} onChange={(e) => onCambio({ ...filtros, informe: e.target.value })}>
          <option value="">Todos los informes</option>
          {(informes.data ?? []).map((i) => (
            <option key={i.id} value={i.id}>
              {i.numero_informe ?? `Informe ${i.id}`}
            </option>
          ))}
        </NativeSelect>,
      )}
      {campo(
        "Prompt",
        <NativeSelect aria-label="Prompt" size="sm" className="h-[2.375rem] rounded-md text-[0.78125rem]" value={filtros.funcion} onChange={(e) => onCambio({ ...filtros, funcion: e.target.value, revision: "" })}>
          <option value="">Todos los prompts</option>
          {(prompts.data?.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.nombre}
            </option>
          ))}
        </NativeSelect>,
      )}
      {campo(
        "Versión",
        <NativeSelect aria-label="Versión" size="sm" className="h-[2.375rem] rounded-md text-[0.78125rem]" disabled={filtros.funcion === ""} value={filtros.revision} onChange={(e) => onCambio({ ...filtros, revision: e.target.value })}>
          <option value="">Todas las versiones</option>
          {(revisiones.data ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {etiquetaDeRevision(revisiones.data ?? [], r.id)}
            </option>
          ))}
        </NativeSelect>,
      )}
      {campo(
        "Estado",
        <NativeSelect aria-label="Estado" size="sm" className="h-[2.375rem] rounded-md text-[0.78125rem]" value={filtros.estado} onChange={(e) => onCambio({ ...filtros, estado: e.target.value as EstadoFiltro })}>
          <option value="cualquiera">Todos los estados</option>
          <option value="con_error">Con error</option>
          <option value="sin_error">Sin error</option>
        </NativeSelect>,
      )}
      <span className="text-ink-muted ml-auto font-mono text-[0.71875rem] font-bold">{resumen}</span>
    </div>
  );
}

function TarjetaDeCorrida({
  corrida,
  llamadaElegida,
  onLlamada,
}: {
  corrida: CorridaDeLaLista;
  llamadaElegida: number | null;
  onLlamada: (id: number) => void;
}) {
  const [abierta, setAbierta] = useState(false);
  const detalle = useCorrida(abierta ? corrida.id : null);

  return (
    <div className="border-border overflow-hidden rounded-[0.875rem] border-[1.5px]">
      <button
        type="button"
        aria-expanded={abierta}
        onClick={() => setAbierta((a) => !a)}
        className="bg-superficie-tenue flex w-full items-start gap-2 px-3 py-2.5 text-left"
      >
        <ChevronRightIcon aria-hidden className={cn("text-ink-medio mt-0.5 size-3.5 shrink-0 transition-transform", abierta && "rotate-90")} />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className="font-mono text-[0.75rem] font-bold">{corrida.informe?.numero ?? "Sin informe"}</span>
            <span className="text-ink-secundario truncate text-[0.75rem] font-bold">{corrida.motivo}</span>
          </span>
          <span className="text-ink-medio block font-mono text-[0.65625rem] font-semibold">
            {fechaCortaDeLaCorrida(corrida.iniciada_en)} · {plural(corrida.llamadas, "llamada", "llamadas")} ·{" "}
            {formatearDuracion(corrida.duracion_s === null ? null : corrida.duracion_s * 1000)} · {formatearCostoUsd(corrida.costo_usd)}
          </span>
        </span>
        {corrida.errores > 0 ? (
          <Etiqueta tono="danger" size="sm" className="text-[0.59375rem]">
            {plural(corrida.errores, "error", "errores")}
          </Etiqueta>
        ) : null}
      </button>

      {abierta ? (
        <div className="flex flex-col gap-1.5 px-3 py-2.5">
          {detalle.isPending ? (
            <Skeleton className="h-12" />
          ) : detalle.isError ? (
            <p className="text-meta text-danger-texto">No pudimos cargar la corrida.</p>
          ) : (
            detalle.data.pasos.map((paso, i) => (
              <div key={paso.id ?? `suelta-${i}`} className="flex flex-col gap-0.5">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-ink-secundario text-[0.71875rem] font-extrabold">{paso.nombre ?? "Otras llamadas"}</span>
                  <span className="text-ink-medio font-mono text-[0.65625rem] font-semibold">
                    {plural(paso.resumen.llamadas, "llamada", "llamadas")} · {formatearDuracion(paso.duracion_ms ?? paso.resumen.duracion_ms)}
                  </span>
                </div>
                {paso.llamadas.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => onLlamada(l.id)}
                    aria-pressed={llamadaElegida === l.id}
                    className={cn(
                      "flex min-h-[2.125rem] items-center gap-2 rounded-[0.5625rem] pr-2 pl-[1.125rem] text-left hover:bg-superficie-tenue",
                      llamadaElegida === l.id && "bg-divisor hover:bg-divisor",
                    )}
                  >
                    <span aria-hidden className={cn("size-[0.4375rem] shrink-0 rounded-full", PUNTO[l.estado])} />
                    <span className="min-w-0 flex-1 truncate text-[0.75rem] font-semibold">{l.nombre}</span>
                    <span className="text-ink-medio font-mono text-[0.65625rem]">{formatearDuracion(l.duracion_ms)}</span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
